import { Feather } from "@expo/vector-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Image,
  PanResponder,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";
import type { ProductPlacement, ShopProduct } from "../../types";
import FloatingPortal from "./FloatingPortal";
import {
  fetchShopProducts,
  fetchVideoProductEditor,
  saveVideoProductEditor,
} from "../services/videoServer";

function placementId(productId: string) {
  return `${productId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function mergeProductLibrary(
  products: ShopProduct[],
  placements: ProductPlacement[],
) {
  const merged = new Map(products.map((product) => [product.productId, product]));
  for (const placement of placements) {
    if (merged.has(placement.productId)) continue;
    const {
      placementId: _placementId,
      startSeconds: _startSeconds,
      endSeconds: _endSeconds,
      enabled: _enabled,
      ...product
    } = placement;
    merged.set(product.productId, product);
  }
  return [...merged.values()];
}

export default function ProductPlacementEditor({
  visible,
  videoId,
  videoTitle,
  currentTimeSeconds,
  initialPlacements,
  onClose,
  onSaved,
}: {
  visible: boolean;
  videoId: string;
  videoTitle: string;
  currentTimeSeconds: number;
  initialPlacements: ProductPlacement[];
  onClose: () => void;
  onSaved: (placements: ProductPlacement[]) => void;
}) {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [placements, setPlacements] = useState(initialPlacements);
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [query, setQuery] = useState("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [startSeconds, setStartSeconds] = useState("0");
  const [endSeconds, setEndSeconds] = useState("15");
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const panelPosition = useRef(new Animated.ValueXY()).current;
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (_event, gesture) =>
        Math.abs(gesture.dx) > 3 || Math.abs(gesture.dy) > 3,
      onPanResponderGrant: () => panelPosition.extractOffset(),
      onPanResponderMove: Animated.event(
        [null, { dx: panelPosition.x, dy: panelPosition.y }],
        { useNativeDriver: false },
      ),
      onPanResponderRelease: () => panelPosition.flattenOffset(),
      onPanResponderTerminate: () => panelPosition.flattenOffset(),
    }),
  ).current;

  const resetForm = (start = currentTimeSeconds) => {
    setEditingIndex(null);
    setSelectedProductId("");
    setStartSeconds(start.toFixed(1));
    setEndSeconds((start + 15).toFixed(1));
  };

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    panelPosition.setOffset({ x: 0, y: 0 });
    panelPosition.setValue({ x: 0, y: 0 });
    setPlacements(initialPlacements);
    setProducts([]);
    setQuery("");
    resetForm(currentTimeSeconds);
    setLoadingProducts(true);
    setMessage("正在验证权限并读取商品库…");

    Promise.allSettled([
      fetchVideoProductEditor(videoId),
      fetchShopProducts(),
    ]).then(([editorResult, productResult]) => {
      if (cancelled) return;
      setLoadingProducts(false);
      if (editorResult.status === "rejected") {
        setMessage(
          editorResult.reason instanceof Error
            ? editorResult.reason.message
            : "无权编辑此视频商品",
        );
        return;
      }
      const nextPlacements = editorResult.value.productPlacements;
      const library = [
        ...editorResult.value.products,
        ...(productResult.status === "fulfilled" ? productResult.value : []),
      ];
      const mergedProducts = mergeProductLibrary(library, nextPlacements);
      setPlacements(nextPlacements);
      setProducts(mergedProducts);
      if (productResult.status === "rejected" && !mergedProducts.length) {
        setMessage("商品库读取失败，请稍后重试");
      } else if (!mergedProducts.length) {
        setMessage("商品库暂无可用商品，请先在后台商品库维护商品");
      } else {
        setMessage("");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [visible, videoId]);

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return products;
    return products.filter((product) =>
      `${product.name} ${product.subtitle} ${product.productId}`
        .toLowerCase()
        .includes(normalizedQuery),
    );
  }, [products, query]);

  const selectedProduct = useMemo(
    () => products.find((product) => product.productId === selectedProductId),
    [products, selectedProductId],
  );

  const editPlacement = (placement: ProductPlacement, index: number) => {
    setEditingIndex(index);
    setSelectedProductId(placement.productId);
    setStartSeconds(String(placement.startSeconds));
    setEndSeconds(String(placement.endSeconds));
    setMessage("正在编辑此商品的显示时间段");
  };

  const commitPlacement = () => {
    if (!selectedProduct) {
      setMessage("请先从商品库选择一个商品");
      return;
    }
    const start = Math.max(0, Number(startSeconds) || 0);
    const end = Math.max(0, Number(endSeconds) || 0);
    if (end <= start) {
      setMessage("消失时间必须晚于显示时间");
      return;
    }
    const current = editingIndex === null ? null : placements[editingIndex];
    const placement: ProductPlacement = {
      ...selectedProduct,
      placementId:
        current?.productId === selectedProduct.productId
          ? current.placementId
          : placementId(selectedProduct.productId),
      startSeconds: Number(start.toFixed(3)),
      endSeconds: Number(end.toFixed(3)),
      enabled: true,
    };
    setPlacements((items) =>
      editingIndex === null
        ? [...items, placement]
        : items.map((item, index) =>
            index === editingIndex ? placement : item,
          ),
    );
    resetForm(currentTimeSeconds);
    setMessage("商品时间点已加入，点击“保存到视频”后生效");
  };

  const save = async () => {
    setSaving(true);
    setMessage("正在保存商品与显示时间点…");
    try {
      const saved = await saveVideoProductEditor(videoId, placements);
      setPlacements(saved);
      onSaved(saved);
      onClose();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "保存失败");
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  const floatingAnchor =
    Platform.OS === "web"
      ? ({ position: "fixed", top: 74, right: 22 } as never)
      : ({ position: "absolute", top: 60, right: 12 } as never);
  const panelWidth = Math.min(680, Math.max(300, windowWidth - 28));
  const panelHeight = Math.max(390, windowHeight - 104);

  return (
    <FloatingPortal>
      <Animated.View
      accessibilityViewIsModal={false}
      style={[
        styles.card,
        floatingAnchor,
        {
          width: panelWidth,
          maxHeight: panelHeight,
          transform: panelPosition.getTranslateTransform(),
        },
      ]}
    >
      <View style={styles.header}>
        <View style={styles.dragRegion} {...panResponder.panHandlers}>
          <View style={styles.dragHandle}>
            <Feather name="move" size={18} color="#28c99a" />
          </View>
          <View style={styles.headerCopy}>
            <Text style={styles.permission}>拖动此栏移动窗口 · 背景视频可继续操作</Text>
            <Text style={styles.title}>从商品库插入</Text>
            <Text style={styles.videoTitle} numberOfLines={1}>
              {videoTitle}
            </Text>
          </View>
        </View>
        <Pressable style={styles.close} onPress={onClose}>
          <Feather name="x" size={22} color="#aeb8b4" />
        </Pressable>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.libraryHeader}>
          <View>
            <Text style={styles.sectionTitle}>商品库</Text>
            <Text style={styles.sectionHint}>只能选择已有商品，不能在这里新建</Text>
          </View>
          <Text style={styles.libraryCount}>{products.length} 件</Text>
        </View>
        <View style={styles.searchBox}>
          <Feather name="search" size={15} color="#70827a" />
          <TextInput
            value={query}
            onChangeText={setQuery}
            style={styles.searchInput}
            placeholder="搜索商品名称或 ID"
            placeholderTextColor="#617069"
          />
        </View>
        <ScrollView
          style={styles.productList}
          contentContainerStyle={styles.productListContent}
          nestedScrollEnabled
        >
          {filteredProducts.map((product) => {
            const selected = selectedProductId === product.productId;
            return (
              <Pressable
                key={product.productId}
                style={[styles.productRow, selected && styles.productRowSelected]}
                onPress={() => {
                  setSelectedProductId(product.productId);
                  setMessage("");
                }}
              >
                {product.imageUrl ? (
                  <Image source={{ uri: product.imageUrl }} style={styles.productImage} />
                ) : (
                  <View style={styles.productImageFallback}>
                    <Feather name="shopping-bag" size={18} color="#779187" />
                  </View>
                )}
                <View style={styles.productCopy}>
                  <Text style={styles.productName} numberOfLines={1}>
                    {product.name}
                  </Text>
                  <Text style={styles.productMeta} numberOfLines={1}>
                    {product.pointPrice} 点 · 库存 {product.stock} · {product.productId}
                  </Text>
                </View>
                <View style={[styles.radio, selected && styles.radioSelected]}>
                  {selected ? <View style={styles.radioDot} /> : null}
                </View>
              </Pressable>
            );
          })}
          {!loadingProducts && !filteredProducts.length ? (
            <View style={styles.emptyLibrary}>
              <Feather name="package" size={24} color="#60736b" />
              <Text style={styles.emptyLibraryText}>
                {products.length ? "没有匹配商品" : "商品库暂无可用商品"}
              </Text>
            </View>
          ) : null}
        </ScrollView>

        <View style={styles.timePanel}>
          <View style={styles.timePanelTitleRow}>
            <Text style={styles.sectionTitle}>显示与消失时间点</Text>
            <Text style={styles.currentTime}>
              当前播放 {currentTimeSeconds.toFixed(1)} 秒
            </Text>
          </View>
          {selectedProduct ? (
            <View style={styles.selectedSummary}>
              <Feather name="check-circle" size={15} color="#28c99a" />
              <Text style={styles.selectedSummaryText} numberOfLines={1}>
                已选择：{selectedProduct.name}
              </Text>
            </View>
          ) : null}
          <View style={styles.timeGrid}>
            <View style={styles.timeField}>
              <Text style={styles.label}>显示时间（秒）</Text>
              <TextInput
                value={startSeconds}
                onChangeText={setStartSeconds}
                keyboardType="decimal-pad"
                style={styles.input}
              />
              <Pressable
                style={styles.useCurrent}
                onPress={() => setStartSeconds(currentTimeSeconds.toFixed(1))}
              >
                <Text style={styles.useCurrentText}>取当前时间</Text>
              </Pressable>
            </View>
            <View style={styles.timeField}>
              <Text style={styles.label}>消失时间（秒）</Text>
              <TextInput
                value={endSeconds}
                onChangeText={setEndSeconds}
                keyboardType="decimal-pad"
                style={styles.input}
              />
              <Pressable
                style={styles.useCurrent}
                onPress={() => setEndSeconds(currentTimeSeconds.toFixed(1))}
              >
                <Text style={styles.useCurrentText}>取当前时间</Text>
              </Pressable>
            </View>
          </View>
          <View style={styles.formActions}>
            {editingIndex !== null ? (
              <Pressable
                style={styles.cancelEdit}
                onPress={() => resetForm(currentTimeSeconds)}
              >
                <Text style={styles.cancelEditText}>取消编辑</Text>
              </Pressable>
            ) : null}
            <Pressable style={styles.addButton} onPress={commitPlacement}>
              <Feather
                name={editingIndex === null ? "plus" : "check"}
                size={17}
                color="#17120b"
              />
              <Text style={styles.addButtonText}>
                {editingIndex === null ? "加入此时间段" : "更新时间段"}
              </Text>
            </Pressable>
          </View>
        </View>

        <View style={styles.listHeader}>
          <Text style={styles.sectionTitle}>已插入商品（{placements.length}）</Text>
          <Text style={styles.listHint}>点击可修改时间</Text>
        </View>
        {placements.map((placement, index) => (
          <Pressable
            key={placement.placementId}
            style={[
              styles.placementRow,
              editingIndex === index && styles.placementRowEditing,
            ]}
            onPress={() => editPlacement(placement, index)}
          >
            <View style={styles.placementCopy}>
              <Text style={styles.placementName} numberOfLines={1}>
                {placement.name}
              </Text>
              <Text style={styles.placementMeta}>
                显示 {placement.startSeconds}s · 消失 {placement.endSeconds}s · {placement.pointPrice} 点
              </Text>
            </View>
            <Pressable
              style={styles.removeButton}
              onPress={(event) => {
                event.stopPropagation?.();
                setPlacements((items) =>
                  items.filter((_, itemIndex) => itemIndex !== index),
                );
                if (editingIndex === index) resetForm(currentTimeSeconds);
              }}
            >
              <Feather name="trash-2" size={16} color="#ffaaa7" />
            </Pressable>
          </Pressable>
        ))}
        {message ? <Text style={styles.message}>{message}</Text> : null}
      </ScrollView>

      <View style={styles.footer}>
        <Text style={styles.footerHint}>窗口外可继续播放、拖放和切换视频</Text>
        <Pressable style={styles.footerCancel} onPress={onClose}>
          <Text style={styles.footerCancelText}>取消</Text>
        </Pressable>
        <Pressable
          disabled={saving}
          style={[styles.saveButton, saving && styles.disabled]}
          onPress={() => void save()}
        >
          <Feather name="save" size={17} color="#17120b" />
          <Text style={styles.saveButtonText}>
            {saving ? "正在保存…" : "保存到视频"}
          </Text>
        </Pressable>
      </View>
      </Animated.View>
    </FloatingPortal>
  );
}

const styles = StyleSheet.create({
  card: {
    zIndex: 10000,
    overflow: "hidden",
    borderRadius: 16,
    backgroundColor: "#07130f",
    borderWidth: 1,
    borderColor: "#3f806a",
    shadowColor: "#000",
    shadowOpacity: 0.42,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 12 },
    elevation: 28,
  },
  header: {
    minHeight: 74,
    paddingHorizontal: 15,
    paddingVertical: 11,
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#245142",
    backgroundColor: "#0a1d16",
  },
  dragRegion: { flex: 1, minWidth: 0, flexDirection: "row", alignItems: "center", gap: 11 },
  dragHandle: { width: 34, height: 34, borderRadius: 10, alignItems: "center", justifyContent: "center", backgroundColor: "#123d2f" },
  headerCopy: { flex: 1, minWidth: 0 },
  permission: { color: "#28c99a", fontSize: 9, fontWeight: "900" },
  title: { marginTop: 2, color: "#f1f5f3", fontSize: 19, fontWeight: "900" },
  videoTitle: { marginTop: 2, color: "#81918b", fontSize: 10 },
  close: { width: 38, height: 38, alignItems: "center", justifyContent: "center" },
  scroll: { flexShrink: 1 },
  content: { padding: 15, paddingBottom: 22 },
  libraryHeader: { flexDirection: "row", alignItems: "flex-end", justifyContent: "space-between", gap: 12 },
  sectionTitle: { color: "#eaf0ed", fontSize: 14, fontWeight: "900" },
  sectionHint: { marginTop: 3, color: "#73837d", fontSize: 10 },
  libraryCount: { color: "#d7b65d", fontSize: 11, fontWeight: "900" },
  searchBox: { height: 40, marginTop: 10, paddingHorizontal: 11, flexDirection: "row", alignItems: "center", gap: 8, borderRadius: 9, backgroundColor: "#05100d", borderWidth: 1, borderColor: "#315c4e" },
  searchInput: { flex: 1, height: 38, color: "#eef3f1", outlineStyle: "none" } as never,
  productList: { maxHeight: 198, marginTop: 9, borderRadius: 11, backgroundColor: "#06110d", borderWidth: 1, borderColor: "#203e34" },
  productListContent: { padding: 7 },
  productRow: { minHeight: 58, marginBottom: 6, padding: 7, flexDirection: "row", alignItems: "center", gap: 10, borderRadius: 9, borderWidth: 1, borderColor: "#213d34", backgroundColor: "#0b1b15" },
  productRowSelected: { borderColor: "#28c99a", backgroundColor: "#102a21" },
  productImage: { width: 44, height: 44, borderRadius: 8, backgroundColor: "#13251f" },
  productImageFallback: { width: 44, height: 44, borderRadius: 8, alignItems: "center", justifyContent: "center", backgroundColor: "#13251f" },
  productCopy: { flex: 1, minWidth: 0 },
  productName: { color: "#edf2f0", fontSize: 12, fontWeight: "800" },
  productMeta: { marginTop: 5, color: "#9eaeaa", fontSize: 9 },
  radio: { width: 20, height: 20, borderRadius: 10, alignItems: "center", justifyContent: "center", borderWidth: 1, borderColor: "#49635a" },
  radioSelected: { borderColor: "#28c99a" },
  radioDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: "#28c99a" },
  emptyLibrary: { minHeight: 112, alignItems: "center", justifyContent: "center", gap: 8 },
  emptyLibraryText: { color: "#78877f", fontSize: 11 },
  timePanel: { marginTop: 14, padding: 13, borderRadius: 12, backgroundColor: "#0e241c", borderWidth: 1, borderColor: "#2b6954" },
  timePanelTitleRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 10 },
  currentTime: { color: "#d7b65d", fontSize: 10, fontWeight: "800" },
  selectedSummary: { marginTop: 9, flexDirection: "row", alignItems: "center", gap: 7 },
  selectedSummaryText: { flex: 1, color: "#a8d9c8", fontSize: 10, fontWeight: "700" },
  timeGrid: { marginTop: 11, flexDirection: "row", flexWrap: "wrap", gap: 10 },
  timeField: { flex: 1, minWidth: 190 },
  label: { marginBottom: 5, color: "#9faaa6", fontSize: 10, fontWeight: "700" },
  input: { width: "100%", height: 38, paddingHorizontal: 10, borderRadius: 8, color: "#f1f5f3", backgroundColor: "#05100d", borderWidth: 1, borderColor: "#315c4e" },
  useCurrent: { alignSelf: "flex-start", marginTop: 6, paddingHorizontal: 8, paddingVertical: 5, borderRadius: 6, backgroundColor: "#164435" },
  useCurrentText: { color: "#9ce6cc", fontSize: 9, fontWeight: "800" },
  formActions: { marginTop: 12, flexDirection: "row", justifyContent: "flex-end", gap: 8 },
  addButton: { height: 38, paddingHorizontal: 14, borderRadius: 8, flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 6, backgroundColor: "#d7b65d" },
  addButtonText: { color: "#17120b", fontSize: 11, fontWeight: "900" },
  cancelEdit: { height: 38, paddingHorizontal: 12, borderRadius: 8, alignItems: "center", justifyContent: "center", backgroundColor: "#15241f" },
  cancelEditText: { color: "#aeb9b5", fontSize: 11, fontWeight: "700" },
  listHeader: { marginTop: 17, marginBottom: 8, flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  listHint: { color: "#6d7a75", fontSize: 9 },
  placementRow: { minHeight: 57, marginBottom: 7, paddingHorizontal: 11, paddingVertical: 8, borderRadius: 9, flexDirection: "row", alignItems: "center", gap: 9, backgroundColor: "#0c1c17", borderWidth: 1, borderColor: "#24483b" },
  placementRowEditing: { borderColor: "#d7b65d" },
  placementCopy: { flex: 1, minWidth: 0 },
  placementName: { color: "#e9efec", fontSize: 12, fontWeight: "800" },
  placementMeta: { marginTop: 4, color: "#d4b871", fontSize: 9 },
  removeButton: { width: 32, height: 32, borderRadius: 16, alignItems: "center", justifyContent: "center", backgroundColor: "#321d1b" },
  message: { marginTop: 11, color: "#e2c27b", fontSize: 10, textAlign: "center" },
  footer: { minHeight: 62, paddingHorizontal: 14, flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: 8, borderTopWidth: 1, borderTopColor: "#23473b", backgroundColor: "#081711" },
  footerHint: { flex: 1, color: "#6d7d76", fontSize: 9 },
  footerCancel: { height: 38, paddingHorizontal: 14, borderRadius: 8, alignItems: "center", justifyContent: "center", borderWidth: 1, borderColor: "#36564b" },
  footerCancelText: { color: "#abb6b2", fontSize: 11, fontWeight: "700" },
  saveButton: { height: 38, paddingHorizontal: 15, borderRadius: 8, flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 7, backgroundColor: "#d7b65d" },
  saveButtonText: { color: "#17120b", fontSize: 12, fontWeight: "900" },
  disabled: { opacity: 0.5 },
});
